import { ValidationSchemas, lookupConfig } from "./formConstants";
import { z } from "zod";

export const waterLevelsFormConfig = (userId = "") => [
  {
    title: "General Information",
    fields: [
      {
        name: "collect_timestamp",
        label: "Collection Date/Time",
        type: "dateTime",
        validation: ValidationSchemas.datetime,
        defaultValue: new Date().toISOString(),
      },
      {
        name: "is_pumping",
        label: "Well Pumping?",
        type: "checkbox",
        validation: ValidationSchemas.boolean,
        defaultValue: true,
      },
      {
        name: "has_eline_access_port",
        label: "E-Line Access Port?",
        type: "checkbox",
        validation: ValidationSchemas.boolean,
        defaultValue: false,
      },
      {
        name: "has_conductor_pipe",
        label: "Conductor Pipe?",
        type: "checkbox",
        validation: ValidationSchemas.boolean,
        defaultValue: false,
      },
    ],
  },
  {
    title: "Measurement Details",
    fields: [
      {
        name: "wl_meas_method_ndx",
        label: "Water Level Measurement Method",
        type: "selectSingle",
        lookupTable: lookupConfig.waterLevelMeasurementMethods,
        validation: ValidationSchemas.positiveInt,
      },
      {
        name: "wl_meas_from_ndx",
        label: "Measured From",
        type: "selectSingle",
        lookupTable: lookupConfig.waterLevelMeasuredFrom,
        validation: ValidationSchemas.positiveInt,
        defaultValue: 1,
      },
      {
        name: "aqsci_data_source_ndx",
        label: "Source of Measurements",
        type: "selectSingle",
        lookupTable: lookupConfig.waterLevelSourceOfMeasurements,
        validation: ValidationSchemas.positiveInt,
      },
      {
        name: "aqsci_equipment_ndx",
        label: "Equipment Used",
        type: "selectSingle",
        lookupTable: lookupConfig.wlEquipment,
        validation: ValidationSchemas.positiveInt,
      },
      {
        name: "equipment_other_desc",
        label: "Equipment Description (Other)",
        type: "text",
        validation: ValidationSchemas.string.nullable().optional(),
        showIf: {
          deps: ["aqsci_equipment_ndx"],
          fn: (values, lookupsData) => {
            const equipmentOptions =
              lookupsData?.["ui_list_wl_equipment"] || [];
            const selected = equipmentOptions.find(
              (opt) => opt.id === values.aqsci_equipment_ndx
            );
            return selected?.is_other === true;
          },
          resetValue: null,
          required: true,
        },
      },
      {
        name: "wl_meas_point_height_in",
        label: "Measurement Point Height (inches)",
        type: "number",
        validation: z.number().catch(0),
        defaultValue: 0,
      },
    ],
  },
  {
    title: "Water Level Readings",
    fields: [
      {
        name: "wl_meas_val_ft",
        label: "Depth to Groundwater (ft)",
        type: "number",
        validation: z.number(),
      },
      {
        name: "_final_dtw",
        label: "Final Depth to Groundwater (ft)",
        type: "computed",
        compute: {
          deps: ["wl_meas_val_ft", "wl_meas_point_height_in"],
          fn: (values) => {
            const dtw = values.wl_meas_val_ft;
            const mpHeight = values.wl_meas_point_height_in;
            if (dtw === null || dtw === undefined || isNaN(dtw)) return null;
            const mpFt = (mpHeight || 0) / 12;
            return dtw - mpFt;
          },
          decimals: 2,
        },
        infoTooltip:
          "Calculated: Depth to Groundwater minus Measurement Point Height",
      },
      {
        name: "wl_meas_val_ft_2",
        label: "Depth to Groundwater (ft) - Optional 1",
        type: "number",
        validation: ValidationSchemas.number,
      },
      {
        name: "_final_dtw_2",
        label: "Final DTW - Optional 1 (ft)",
        type: "computed",
        compute: {
          deps: ["wl_meas_val_ft_2", "wl_meas_point_height_in"],
          fn: (values) => {
            const dtw = values.wl_meas_val_ft_2;
            const mpHeight = values.wl_meas_point_height_in;
            if (dtw === null || dtw === undefined || isNaN(dtw)) return null;
            const mpFt = (mpHeight || 0) / 12;
            return dtw - mpFt;
          },
          decimals: 2,
        },
        showIf: {
          deps: ["wl_meas_val_ft_2"],
          fn: (values) =>
            values.wl_meas_val_ft_2 !== null &&
            values.wl_meas_val_ft_2 !== undefined &&
            !isNaN(values.wl_meas_val_ft_2),
        },
      },
      {
        name: "wl_meas_val_ft_3",
        label: "Depth to Groundwater (ft) - Optional 2",
        type: "number",
        validation: ValidationSchemas.number,
      },
      {
        name: "_final_dtw_3",
        label: "Final DTW - Optional 2 (ft)",
        type: "computed",
        compute: {
          deps: ["wl_meas_val_ft_3", "wl_meas_point_height_in"],
          fn: (values) => {
            const dtw = values.wl_meas_val_ft_3;
            const mpHeight = values.wl_meas_point_height_in;
            if (dtw === null || dtw === undefined || isNaN(dtw)) return null;
            const mpFt = (mpHeight || 0) / 12;
            return dtw - mpFt;
          },
          decimals: 2,
        },
        showIf: {
          deps: ["wl_meas_val_ft_3"],
          fn: (values) =>
            values.wl_meas_val_ft_3 !== null &&
            values.wl_meas_val_ft_3 !== undefined &&
            !isNaN(values.wl_meas_val_ft_3),
        },
      },
    ],
  },
  {
    title: "Additional Information",
    fields: [
      {
        name: "remarks",
        label: "Notes",
        type: "text",
        validation: ValidationSchemas.string.nullable().optional(),
      },
      {
        name: "sampled_by",
        label: "Sampled By",
        type: "text",
        validation: ValidationSchemas.string,
        defaultValue: userId,
        readOnly: true,
        adminOnly: true,
      },
    ],
  },
];
